import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	INVESTMENT_PROGRAMME_FORM_FIELDS,
	InvestmentProgrammeFormValues,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/InvestmentProgrammeDrawer/utils/types.ts';
import { PersonDataContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/hooks/usePersonData.ts';
import { getAvailablePersonTransactionsForMipRequest } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/InvestmentProgrammeDrawer/apis/useInvestmentProgramme.ts';
import { InvestmentProgrammeFormShape } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/InvestmentProgrammeDrawer/utils/relatedPeopleShape.ts';
import { linkPersonToInvestmentProgrammeRequest } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/apis/useInvestmentProgrammes.ts';

export const useLinkInvestmentProgrammeDrawer = (onClose: () => void) => {
	const { selectedEntityId } = useContext(CapManageControllerContext);
	const { refetchInvestmentProgrammes } = useContext(PersonDataContext);
	const [linkTransactions, setLinkTransactions] = useState(false);

	const linkPersonToMipMutation = useMutation({
		mutationFn: linkPersonToInvestmentProgrammeRequest,
	});

	const linkMipForm = useForm<InvestmentProgrammeFormValues>({
		mode: 'all',
		resolver: zodResolver(InvestmentProgrammeFormShape),
	});

	const mipId = useWatch({
		control: linkMipForm.control,
		name: INVESTMENT_PROGRAMME_FORM_FIELDS.mip_id,
	});

	const availablePersonTransactionsForMipRequest = useQuery({
		queryKey: ['availablePersonTransactionsForMip', selectedEntityId, mipId],
		queryFn: () => {
			if (!selectedEntityId) {
				return Promise.resolve([]);
			}

			return getAvailablePersonTransactionsForMipRequest({
				personId: selectedEntityId,
				mipId,
			});
		},
		enabled: !!(selectedEntityId && mipId),
	});

	const onFormSubmit = async (formValues: InvestmentProgrammeFormValues) => {
		const { mip_id } = formValues;
		return linkPersonToMipMutation.mutateAsync(
			{
				personId: selectedEntityId,
				mipId: mip_id,
				linkTransactions,
			},
			{
				onSuccess: () => {
					void refetchInvestmentProgrammes();
					setLinkTransactions(false);
					onClose();
				},
			}
		);
	};

	return {
		linkMipForm,
		onFormSubmit,
		isLoading: linkPersonToMipMutation.isPending,
		personTransactionForMip: availablePersonTransactionsForMipRequest.data,
		linkTransactions,
		setLinkTransactions,
		selectedEntityId,
	};
};
